import { useEffect, useMemo, useRef, useState } from 'react';
import layout from '../layout/layout';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import {
  getChatConversations,
  type ChatConversationItem,
} from '../Store/StudentSlices/chatListSlice';
import {
  getChatHistory,
  sendChatMessage,
  type ChatMessage,
} from '../Store/StudentSlices/chatSlice';

type Conversation = {
  id: string;
  name: string;
  avatar?: string;
  preview: string;
  time: string;
  unread: number;
  status: 'Active' | 'Away';
};

type Message = {
  id: string;
  text: string;
  time: string;
  isMe: boolean;
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

const formatChatTime = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const mapChatMessage = (
  message: ChatMessage,
  coachId?: string
): Message => {
  const isCoach =
    message.sender_role === 'coach' ||
    (coachId ? message.sender_id === coachId : false);
  return {
    id: message.id,
    text: message.message,
    time: formatChatTime(message.timestamp),
    isMe: isCoach,
  };
};

const toConversation = (item: ChatConversationItem): Conversation => {
  const name = item.partner.name?.trim() || 'Conversation';
  return {
    id: item.partner.id,
    name,
    avatar: item.partner.avatar,
    preview: item.last_message?.message ?? 'No messages yet.',
    time: formatChatTime(item.last_message?.timestamp),
    unread: item.unread_count ?? 0,
    status: 'Active',
  };
};

const Messages = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get('id');
  const { messages, histories, loadingHistory, sending } = useAppSelector(
    (state) => state.chat
  );
  const {
    chats,
    loading: loadingChatList,
    error: chatListError,
    errorMessage: chatListErrorMessage,
  } = useAppSelector((state) => state.chatList);
  const { coachInfo } = useAppSelector((state) => state.getCoach);
  const [draft, setDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const coachId = (coachInfo as any)?.id ?? (coachInfo as any)?.user_id ?? '';
  const conversations = useMemo(
    () => chats.map((chat) => toConversation(chat)),
    [chats]
  );
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    const query = searchQuery.toLowerCase();
    return conversations.filter(
      (conv) =>
        conv.name.toLowerCase().includes(query) ||
        conv.preview.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);
  const activeConversation = useMemo(
    () => conversations.find((item) => item.id === activeId) ?? null,
    [activeId, conversations]
  );
  const isChatOpen = Boolean(activeId);
  const isSendDisabled = sending || draft.trim().length === 0;
  const activeName = activeConversation?.name ?? 'Student';
  const mappedMessages = useMemo(() => {
    const conversationMessages = activeId
      ? histories?.[activeId] ?? messages
      : [];
    return conversationMessages.map((message) => mapChatMessage(message, coachId));
  }, [activeId, coachId, histories, messages]);

  useEffect(() => {
    if (chats.length > 0) {
      return;
    }
    dispatch(getChatConversations());
  }, [chats.length, dispatch]);

  useEffect(() => {
    if (!activeId) {
      return;
    }
    // Check if we already have history for this user
    if (histories?.[activeId]) {
      return;
    }
    dispatch(getChatHistory(activeId));
  }, [activeId, dispatch, histories]);

  useEffect(() => {
    // Smooth scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mappedMessages.length]);

  const handleSend = () => {
    if (!activeId) {
      return;
    }
    const trimmed = draft.trim();
    if (!trimmed || sending) {
      return;
    }
    const clientId = `local-${Date.now()}`;
    dispatch(
      sendChatMessage({
        receiver_id: activeId,
        content: trimmed,
        client_id: clientId,
      })
    );
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-6">
      <div className={`flex flex-col gap-4 ${isChatOpen ? 'lg:flex-row' : ''}`}>
        <section
          className={`flex w-full flex-col rounded-[16px] border border-slate-100 bg-white p-4 shadow-sm ${
            isChatOpen ? 'lg:w-[320px] lg:shrink-0' : ''
          }`}
        >
          <label className="flex items-center gap-2 rounded-[10px] border border-slate-100 bg-slate-50 px-3 py-2 text-slate-400">
            <svg
              className="h-4 w-4 text-slate-400"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-3.5-3.5" />
            </svg>
            <input
              className="w-full bg-transparent text-xs font-semibold text-slate-600 outline-none placeholder:text-slate-400"
              placeholder="Search conversations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>

          <div className="mt-4 flex flex-col gap-2">
            {loadingChatList ? (
              <p className="px-3 text-xs text-slate-400">
                Loading conversations...
              </p>
            ) : chatListError ? (
              <p className="px-3 text-xs text-rose-500">
                {chatListErrorMessage || 'Failed to load conversations.'}
              </p>
            ) : filteredConversations.length === 0 ? (
              <p className="px-3 text-xs text-slate-400">
                {searchQuery ? 'No conversations found.' : 'No conversations yet.'}
              </p>
            ) : (
              filteredConversations.map((conversation) => {
                const isActive = conversation.id === activeId;
                return (
                  <button
                    key={conversation.id}
                    type="button"
                    onClick={() => setSearchParams({ id: conversation.id })}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition ${
                      isActive ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="relative">
                      {conversation.avatar ? (
                        <img
                          src={conversation.avatar}
                          alt={conversation.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                          {getInitials(conversation.name)}
                        </div>
                      )}
                      {conversation.unread > 0 ? (
                        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-semibold text-white">
                          {conversation.unread}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900">
                          {conversation.name}
                        </p>
                        <span className="text-[10px] text-slate-400">
                          {conversation.time}
                        </span>
                      </div>
                      <p className="truncate text-xs text-slate-400">
                        {conversation.preview}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {isChatOpen ? (
          <section className="flex min-w-0 flex-1 flex-col rounded-[16px] border border-slate-100 bg-white shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                  {getInitials(activeName)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {activeName}
                  </p>
                  {/* <p className="text-xs text-emerald-600">
                    {activeStatus}
                  </p> */}
                </div>
              </div>
              {/* <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="flex h-8 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 lg:hidden"
                  onClick={() => setSearchParams({})}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                  aria-label="Conversation options"
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="19" r="1" />
                  </svg>
                </button>
              </div> */}
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-6 min-h-0 scroll-smooth">
              {loadingHistory ? (
                <p className="text-sm text-slate-400">Loading messages...</p>
              ) : mappedMessages.length === 0 ? (
                <p className="text-sm text-slate-400">No messages yet.</p>
              ) : (
                mappedMessages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`flex animate-fadeInUp ${
                      message.isMe ? 'justify-end' : 'justify-start'
                    }`}
                    style={{
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'backwards'
                    }}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm transition-all duration-300 ${
                        message.isMe
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <p>{message.text}</p>
                      <p
                        className={`mt-2 text-[10px] ${
                          message.isMe ? 'text-slate-300' : 'text-slate-400'
                        }`}
                      >
                        {message.time}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="flex items-center gap-3 border-t border-slate-100 px-6 py-4 flex-shrink-0 bg-white">
              {/* <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                aria-label="Attach file"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21.44 11.05 12.6 19.9a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a2 2 0 1 1-2.83-2.83l7.78-7.78" />
                </svg>
              </button> */}
              <input
                className="flex-1 rounded-[8px] border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none"
                placeholder="Type your message..."
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSend();
                  }
                }}
              />
              <button
                type="button"
                className="flex h-11 w-12 items-center justify-center rounded-[8px] bg-slate-900 text-white"
                aria-label="Send"
                onClick={handleSend}
                disabled={isSendDisabled}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 2 11 13" />
                  <path d="m22 2-7 20-4-9-9-4Z" />
                </svg>
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default layout(Messages);
